import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import mergeClasses from '@/shared/lib/utils';
import { AlertCircle, CheckCircle, Info, TriangleAlert } from 'lucide-react';
const alertVariants = cva('relative w-full rounded-lg border [&>svg]:absolute [&>svg]:text-foreground ', {
  variants: {
    variant: {
      default: 'bg-accent text-foreground border-none',
      destructive: 'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
      info: 'border-status-tertiary [&>svg]:text-status-tertiary text-status-tertiary rounded-xl bg-accent',
      warning: 'border-status-warning [&>svg]:text-status-warning text-status-warning rounded-xl bg-accent',
      'warning-orange': '[&>svg]:text-primary rounded-xl bg-status-warning-orange-bg border-status-warning-orange-bg',
      'info-blue': '[&>svg]:text-status-info rounded-xl bg-status-info-blue-bg border-status-info-blue-bg'
    },
    size: {
      sm: 'p-3 [&>svg]:left-3 [&>svg]:top-3 [&>svg~*]:pl-7',
      default: 'px-8 py-6 [&>svg]:left-8 [&>svg]:top-6 [&>svg~*]:pl-8'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
const Alert = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants> & {
  icon?: 'info' | 'warning' | 'error' | 'success' | 'none';
}>(({
  className,
  variant,
  size,
  icon = 'none',
  ...props
}, ref) => <div ref={ref} role="alert" className={mergeClasses(alertVariants({
  variant,
  size
}), className)} {...props}>
    {icon === 'info' && <Info className="size-5 p-0.5" />}
    {icon === 'warning' && <TriangleAlert className="size-5 p-0.5" />}
    {icon === 'error' && <AlertCircle className="size-5 p-0.5" />}
    {icon === 'success' && <CheckCircle className="size-5 p-0.5" />}
    {props.children}
  </div>);
Alert.displayName = 'Alert';
const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(({
  className,
  ...props
}, ref) => <h5 ref={ref} className={mergeClasses('mb-1  leading-none tracking-tight', className)} {...props} />);
AlertTitle.displayName = 'AlertTitle';
const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({
  className,
  ...props
}, ref) => <div ref={ref} className={mergeClasses('text-body2 leading-5 [&_p]:leading-relaxed', className)} {...props} />);
AlertDescription.displayName = 'AlertDescription';
export { Alert, AlertDescription, AlertTitle };